import React from 'react';
import { motion } from 'framer-motion';
import { latestReleaseHome } from './releases/latestReleaseHome';

export default function HomeSection({ onNavigate }) {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-black"></div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex justify-center mb-4 mt-6">
              <img 
                src="/images/chip.png" 
                alt="Poppy Poker Logo" 
                className="w-32 h-32 md:w-40 md:h-40 animate-pulse"
                style={{ width: '8rem', height: '8rem' }}
              />
            </div>

            {/* Comparison Link Card */}
            <div className="max-w-5xl mx-auto mb-8">
              <button
                type="button"
                onClick={() => onNavigate && onNavigate('comparison')}
                className="group relative block w-full overflow-hidden rounded-2xl border border-purple-500/35 bg-gradient-to-br from-black/70 via-black/55 to-purple-950/30 text-left shadow-xl shadow-purple-950/40 ring-1 ring-white/5 backdrop-blur-sm transition hover:border-purple-400/60 hover:shadow-purple-900/50 focus:outline-none focus:ring-2 focus:ring-purple-400"
                aria-label="See how Poppy Poker compares with other poker timer apps"
              >
                <div className="grid gap-0 md:grid-cols-12">
                  <div className="relative md:col-span-7">
                    <img
                      src="/images/comparison-thumbnail.png"
                      alt="Mini preview of the Poppy Poker feature comparison table"
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/40 md:to-black/60" />
                  </div>
                  <div className="flex flex-col justify-center gap-4 px-5 py-5 sm:px-6 sm:py-6 md:col-span-5 md:px-7">
                    <span className="w-fit rounded-full bg-purple-500/25 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-purple-200">
                      Feature comparison
                    </span>
                    <h3 className="text-xl font-semibold leading-tight text-white sm:text-2xl">
                      How does Poppy Poker stack up?
                    </h3>
                    <p className="text-sm leading-relaxed text-gray-300 sm:text-base">
                      See a side-by-side comparison with Blinds Are Up, Poker Timer Pro, Poker Clock, and Tournament Director.
                    </p>
                    <span className="mt-1 inline-flex items-center gap-2 text-sm font-semibold text-purple-300 transition group-hover:text-purple-200">
                      View full comparison
                      <span aria-hidden className="text-base leading-none transition-transform group-hover:translate-x-1">→</span>
                    </span>
                  </div>
                </div>
              </button>
            </div>

            <div className="mx-auto mb-8 w-full max-w-5xl overflow-hidden rounded-2xl border border-purple-500/35 bg-gradient-to-br from-black/70 via-black/55 to-purple-950/30 text-left shadow-xl shadow-purple-950/40 ring-1 ring-white/5 backdrop-blur-sm">
              <div className="flex flex-col gap-2 border-b border-purple-500/25 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                <span className="w-fit rounded-full bg-purple-500/25 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-purple-200">
                  Latest · {latestReleaseHome.version}
                </span>
                <time className="text-sm tabular-nums text-gray-400 sm:text-right">{latestReleaseHome.date}</time>
              </div>

              <div className="grid gap-6 px-5 py-5 sm:px-6 sm:py-6 md:grid-cols-12 md:gap-8 md:py-7">
                <div className="flex flex-col gap-4 md:col-span-7">
                  <p className="text-base leading-relaxed text-gray-300">{latestReleaseHome.summary}</p>
                  {latestReleaseHome.highlights?.length > 0 && (
                    <ul className="space-y-2.5 text-sm leading-snug text-gray-400">
                      {latestReleaseHome.highlights.map((line, i) => (
                        <li key={i} className="flex gap-3">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-purple-400" aria-hidden />
                          <span>{line}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="flex flex-col border-t border-purple-500/20 pt-5 md:col-span-5 md:border-l md:border-t-0 md:pl-8 md:pt-0">
                  <h2 className="text-xl font-semibold leading-tight text-white sm:text-2xl">
                    {latestReleaseHome.title}
                  </h2>
                  <button
                    type="button"
                    onClick={() => onNavigate && onNavigate('releases')}
                    className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-purple-500 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-purple-900/40 transition hover:from-purple-500 hover:to-purple-400 sm:w-auto sm:justify-start"
                  >
                    Release notes
                    <span aria-hidden className="text-base leading-none">→</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Video Section - Moved to top */}
            <div className="max-w-lg mx-auto mb-8">
              <h3 className="text-xl font-bold mb-4 text-center text-purple-400">See Poppy Poker in Action</h3>
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe 
                  width="100%" 
                  height="100%" 
                  src="https://www.youtube.com/embed/1JKAOsyzRYo"
                  title="YouTube video player" 
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    borderRadius: '8px',
                    border: '2px solid rgba(67, 5, 114, 0.3)'
                  }}
                ></iframe>
              </div>
            </div>
            
            {/* Download Buttons - Moved to top */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a 
                href="https://apps.apple.com/us/app/poppy-poker/id6739791798" 
                target="_blank" 
                rel="noopener noreferrer"
                className="casino-button" 
                style={{
                  background: 'linear-gradient(135deg,rgb(166, 134, 217) 0%, #5b21b6 100%)',
                  color: 'black',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  display: 'inline-block'
                }}
              >
                🍎 Download for iOS
              </a>
              <a 
                href="https://play.google.com/store/apps/details?id=com.csw.poppypoker" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  background: 'linear-gradient(135deg,rgb(166, 134, 217) 0%, #5b21b6 100%)',
                  color: 'black',
                  border: 'none',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  display: 'inline-block'
                }}
              >
                🤖 Download for Android
              </a>
            </div>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto font-medium">
              The ultimate poker timer app that brings synchronized gameplay and community-driven features to your poker nights.
            </p>
            <div className="max-w-4xl mx-auto text-left space-y-6 mb-8">
              <p className="text-lg text-gray-300">
        Let's be honest, there are hundreds of poker timer apps available. Poppy Poker solves problems that no other timers have been able to!
      </p>
              <p className="text-lg text-gray-300">
        Looking for a game in your local vicinity? Not a problem, Poppy Poker will provide all the information you need, simply turn on your GPS and let us guide you to any locally running games!
      </p>
              <p className="text-lg text-gray-300">
                Tired of asking how long is left on the clock? Constantly wondering what blind level your game is at? Here's how we solve that problem: One person starts a game timer, all other players can install and open the app and all the important information is synchronised on their devices automatically. No QR codes to scan, no web addresses to type, no fiddly/annoying configuration - it just appears on all devices within close vicinity to the hosting venue! When the next level starts all devices are alerted, no more shouting across the room "blinds are up"! The essential app for every poker enthusiast!
      </p>
              <p className="text-lg text-gray-300">
        Whether you're hosting a casual game night or a serious tournament, Poppy Poker ensures your games run smoothly and efficiently. Once an admin has started a game simply open the app and you will be notified about the running game.
      </p>
            </div>
          </motion.div>
        </div>
        
        {/* Casino Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-purple-500/20 rounded-full blur-xl animate-pulse">♠</div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-purple-600/20 rounded-full blur-xl animate-pulse delay-1000">♦</div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-purple-500/20 rounded-full blur-xl animate-pulse delay-500">♣</div>
        <div className="absolute top-1/3 right-1/4 w-24 h-24 bg-purple-600/20 rounded-full blur-xl animate-pulse delay-700">♥</div>
      </section>

      {/* Key Features Preview */}
      <section className="py-20 bg-black/40 border-t border-purple-500/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-purple-600 bg-clip-text text-transparent">Key Features</h2>
            <p className="text-xl text-gray-300">Everything you need for the perfect poker game</p>
          </div>
          
          <div className="max-w-6xl mx-auto mb-12">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Core Synchronization Features */}
              <div className="bg-black/30 rounded-xl p-8 border border-purple-500/20">
                <h3 className="text-2xl font-semibold mb-6 text-purple-400">🎯 Core Synchronization</h3>
                <ul className="text-lg text-gray-300 space-y-3">
                  <li>• <strong>Synchronized game data</strong> across all devices — no one else can match our capability!</li>
                  <li>• <strong>Simultaneous level change alerts</strong> on all connected devices - <span className="font-bold text-yellow-400">this is a game changer!</span></li>
                  <li>• <strong>No QR codes or configuration</strong> - just open the app and join nearby games instantly</li>
                  <li>• <strong>Game restart audio broadcast</strong> when game resumes after breaks</li>
                  <li>• <strong>Low power mode detection</strong> - no more false/missed alerts</li>
                </ul>
              </div>

              {/* Game Management */}
              <div className="bg-black/30 rounded-xl p-8 border border-purple-500/20">
                <h3 className="text-2xl font-semibold mb-6 text-purple-400">🎮 Game Management</h3>
                <ul className="text-lg text-gray-300 space-y-3">
                  <li>• <strong>Three game types:</strong> Instant, Weekly recurring, and One-off scheduled games</li>
                  <li>• <strong>Centrally controlled timers</strong> with automatic blind level increases</li>
                  <li>• <strong>Game breaks support</strong> with pause/resume functionality</li>
                  <li>• <strong>Game audit log</strong> for admin users to track all changes</li>
                  <li>• <strong>Connection stats</strong> available from game screen</li>
                </ul>
              </div>

              {/* Location & Discovery */}
              <div className="bg-black/30 rounded-xl p-8 border border-purple-500/20">
                <h3 className="text-2xl font-semibold mb-6 text-purple-400">📍 Location & Discovery</h3>
                <ul className="text-lg text-gray-300 space-y-3">
                  <li>• <strong>GPS game finder</strong> - locate nearby games within 200 miles</li>
                  <li>• <strong>Venue registration</strong> with zip/postcode for searchable games</li>
                  <li>• <strong>Map links</strong> to display club locations</li>
                  <li>• <strong>Filter games by distance</strong> and separate running/upcoming sections</li>
                  <li>• <strong>One-click access</strong> to critical game information</li>
                </ul>
              </div>

              {/* Community & Social */}
              <div className="bg-black/30 rounded-xl p-8 border border-purple-500/20">
                <h3 className="text-2xl font-semibold mb-6 text-purple-400">👥 Community & Social</h3>
                <ul className="text-lg text-gray-300 space-y-3">
                  <li>• <strong>Player reviews</strong> for each individual game</li>
                  <li>• <strong>Game photo albums</strong> and photo capture during games</li>
                  <li>• <strong>Winner photo capture</strong> and display</li>
                  <li>• <strong>User display name changes</strong> for personalization</li>
                  <li>• <strong>In-game feedback system</strong> for continuous improvement</li>
                </ul>
              </div>
            </div>

            {/* Advanced Features */}
            <div className="bg-black/30 rounded-xl p-8 border border-purple-500/20 mb-8">
              <h3 className="text-2xl font-semibold mb-6 text-purple-400">🚀 Advanced Features</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-xl font-medium text-purple-400 mb-4">📊 Analytics & Reporting</h4>
                  <ul className="text-lg text-gray-300 space-y-2">
                    <li>• <strong>Detailed league tables</strong> based on weekly game reports</li>
                    <li>• <strong>Monthly, quarterly, and annual reports</strong> with in-depth insights</li>
                    <li>• <strong>Top 3 players summary</strong> on venue front page</li>
                    <li>• <strong>Complete player statistics</strong> including placement and prizes</li>
                    <li>• <strong>Club summary pages</strong> with upcoming and completed games</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-xl font-medium text-purple-400 mb-4">🛠️ Tools & Utilities</h4>
                  <ul className="text-lg text-gray-300 space-y-2">
                    <li>• <strong>Pot Split utility</strong> for easy calculations</li>
                    <li>• <strong>New tools section</strong> with useful poker tools</li>
                    <li>• <strong>Club content management</strong> component</li>
                    <li>• <strong>Version check popup</strong> for updates</li>
                    <li>• <strong>Refresh button</strong> on Venues page for error recovery</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Performance & Reliability */}
            <div className="bg-black/30 rounded-xl p-8 border border-purple-500/20">
              <h3 className="text-2xl font-semibold mb-6 text-purple-400">⚡ Performance & Reliability</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <h4 className="text-lg font-medium text-purple-400 mb-3">🔧 Technical Excellence</h4>
                  <ul className="text-gray-300 space-y-1">
                    <li>• Fine-tuned Java engine performance</li>
                    <li>• Minimized websocket traffic</li>
                    <li>• Backend retry mechanisms</li>
                    <li>• Improved message sending performance</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-medium text-purple-400 mb-3">🎨 User Experience</h4>
                  <ul className="text-gray-300 space-y-1">
                    <li>• Major frontend refresh with stylish look</li>
                    <li>• Improved layouts and user interface</li>
                    <li>• New user preference capabilities</li>
                    <li>• Enhanced in-game menu options</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-medium text-purple-400 mb-3">🔐 Security & Privacy</h4>
                  <ul className="text-gray-300 space-y-1">
                    <li>• Forgotten password functionality</li>
                    <li>• Apple and Google login integration</li>
                    <li>• No third-party data sharing</li>
                    <li>• Secure venue and user registration</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-xl text-gray-300 mb-6">
              Download Poppy Poker now and take your poker game to the next level!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-4">
              <a 
                href="https://apps.apple.com/us/app/poppy-poker/id6739791798" 
                target="_blank" 
                rel="noopener noreferrer"
                className="casino-button" 
                style={{
                  background: 'linear-gradient(135deg,rgb(166, 134, 217) 0%, #5b21b6 100%)',
                  color: 'black',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  display: 'inline-block'
                }}
              >
                🍎 Download for iOS
              </a>
              <a 
                href="https://play.google.com/store/apps/details?id=com.csw.poppypoker" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  background: 'linear-gradient(135deg,rgb(166, 134, 217) 0%, #5b21b6 100%)',
                  color: 'black',
                  border: 'none',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  display: 'inline-block'
                }}
              >
                🤖 Download for Android
              </a>
            </div>
            
            {/* Terms & Conditions Link */}
            <div className="text-center">
              <button
                onClick={() => onNavigate && onNavigate('terms')}
                className="text-purple-400 hover:text-purple-400 underline text-sm transition-colors"
              >
                Terms & Conditions
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
